// accounting-ranged.ts — "Ranged" sub-tab of the Accounting modal.
//
// Phase 12.8: a 2-month calendar + date-range picker + summed historical
// accounting across the picked range. Calendar cells > t.ts_crea are
// selectable; cells with entries in t.reset_list get a violet gradient
// (count controls intensity). The summed totals use the per-day diff
// (acc_diff) returned by /api/terminalacc, which is the day-over-day
// delta of the running acc_long bucket — summing diffs over a range
// gives "what happened in this range" rather than "what the cumulative
// bucket looks like at the end of the range".
//
// Range selection rules:
//   - First click sets `from` (start).
//   - Second click sets `to` (end), automatically normalised so from <= to.
//   - Third click resets and starts a new range.
//   - Clicking the same cell twice clears the selection.
//
// Fetch coalescing:
//   - Per-terminal cache: `t._rangedCache: Map<YYYY-MM-DD, NormalisedAcc>`.
//   - On every range change we compute the set of dates in [from..to]
//     that aren't already cached, and if any are missing we issue ONE
//     GET /api/terminalacc?id=N&from=...&to=... to fill the gaps.
//   - Cache is cleared by closeModal / openTerminalImpl (the latter
//     nukes everything; the former is harmless because the modal is
//     re-opened with a fresh terminal anyway).

import { apiFetch, type ApiFetchConfig } from './api';
import { normaliseAccounting, renderAccTable, makeAccHeader, type NormalisedAcc } from './accounting';
import type { TimestopState } from './state';
import type { Terminal } from './types';
import { showSpinner, hideSpinner, setSpinnerMessage } from './spinner';
import { t as i18n } from './i18n';

// ---- Module-scope state for the currently-open Ranged tab ----
// We keep the selection + visible month offset outside of any
// particular DOM node so a re-render of the calendar (e.g. after
// navigating prev/next month) doesn't blow away the in-progress
// range. closeModal() clears it.
interface RangedState {
  from: string | null;   // 'YYYY-MM-DD'
  to:   string | null;
  // The leftmost month currently visible on the calendar. Always the
  // 1st of a month, 'YYYY-MM-01'. The 2nd month is leftMonth + 1.
  leftMonth: string;
  // Per-terminal cache: YYYY-MM-DD -> NormalisedAcc (per-day diff).
  cache: Map<string, NormalisedAcc>;
  // Last fetched range, so we can show a small "fetched" badge.
  lastFetchedAt: string | null;
}
let _state: RangedState | null = null;

function getState(t: Terminal): RangedState {
  if (!_state) {
    _state = {
      from: null,
      to:   null,
      leftMonth: currentMonthStart(),
      cache: new Map(),
      lastFetchedAt: null,
    };
  }
  // Cache is per-terminal — switching terminal? blow it away.
  if (_curTid === undefined || _curTid !== t.id) {
    _state.cache = new Map();
    _curTid = t.id;
  }
  return _state;
}

// Module-scope tracker for the current terminal id; lets getState()
// know when the modal was re-opened on a different terminal so it
// can clear the cache. Reset by clearRangedState().
let _curTid: number | string | undefined = undefined;

export function clearRangedState(): void {
  // Phase 12.14: chart instances must be released here too.
  // Without this, closing the modal leaves Chart.js's internal
  // registry holding canvas references even though the canvas
  // is gone from the DOM. Re-opening a different terminal would
  // create a fresh set, so the leak is bounded — but it still
  // grows over a long session.
  try { destroyRangedCharts(); } catch { /* already cleared */ }
  _state = null;
  _curTid = undefined;
}

// ---- Date helpers ----
function pad2(n: number): string { return (n < 10 ? '0' : '') + n; }
function ymd(d: Date): string { return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()); }
function currentMonthStart(): string {
  const d = new Date();
  return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-01';
}
function parseYmd(s: string): Date {
  // s = 'YYYY-MM-DD' in LOCAL time (matches the calendar grid).
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function addDays(s: string, n: number): string {
  const d = parseYmd(s);
  d.setDate(d.getDate() + n);
  return ymd(d);
}
function addMonths(s: string, n: number): string {
  const d = parseYmd(s + 'T00:00:00');
  d.setMonth(d.getMonth() + n);
  return ymd(d).slice(0, 7) + '-01';
}
function cmpYmd(a: string, b: string): number { return a < b ? -1 : a > b ? 1 : 0; }
function minYmd(a: string, b: string): string { return cmpYmd(a, b) <= 0 ? a : b; }
function maxYmd(a: string, b: string): string { return cmpYmd(a, b) >= 0 ? a : b; }

// Parse a 'YYYY-MM-DD HH:MM:SS' style ts_crea into a 'YYYY-MM-DD'.
function tsCreaToYmd(ts: string | undefined | null): string | null {
  if (!ts) return null;
  // Accept 'YYYY-MM-DD HH:MM:SS', 'YYYY-MM-DDTHH:MM:SS', 'YYYY-MM-DD'.
  const m = String(ts).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

// "Maximum count" in reset_list — used as the denominator of the
// gradient intensity, so the cell with the most resets gets the
// strongest tint and lower counts get progressively weaker tints.
function maxResetCount(t: Terminal): number {
  if (!t.reset_list) return 0;
  let m = 0;
  for (const v of Object.values(t.reset_list)) {
    if (typeof v === 'number' && v > m) m = v;
  }
  return m;
}

// ---- Range fetch + sum ----
async function ensureRangeCached(
  t: Terminal,
  fromYmd: string,
  toYmd: string,
  config: ApiFetchConfig,
  state: TimestopState,
): Promise<void> {
  // Figure out which dates in [from..to] we don't have yet.
  const missing: string[] = [];
  let d = fromYmd;
  const cache = getState(t).cache;
  while (cmpYmd(d, toYmd) <= 0) {
    if (!cache.has(d)) missing.push(d);
    d = addDays(d, 1);
    // Sanity cap — refuse to walk more than ~366 days even if the
    // server doesn't clamp. Past that the calendar UI is wrong anyway.
    if (missing.length > 366) break;
  }
  if (missing.length === 0) return;

  // Coalesce: ask the server for the whole missing span, including
  // any gaps that are already cached. This is what the spec asked
  // for ("only ask for dates range that is missing"). The server
  // filter is cheap; we do at most one request per range change.
  const reqFrom = minYmd(fromYmd, missing[0]);
  const reqTo   = maxYmd(toYmd, missing[missing.length - 1]);

  setSpinnerMessage(i18n('tab_accounting.loading_history'));
  showSpinner(i18n('tab_accounting.loading_history'));
  try {
    const r = await apiFetch(
      'terminalacc?id=' + encodeURIComponent(String(t.id)) +
        '&from=' + encodeURIComponent(reqFrom) +
        '&to='   + encodeURIComponent(reqTo),
      { method: 'GET' },
      config,
    );
    if (!r.ok || !r.data || typeof r.data !== 'object') {
      throw new Error(((r.data as any)?.error as string) || 'fetch_failed');
    }
    const payload = r.data as {
      records?: Array<{ dated: string; acc_long: unknown; acc_diff: unknown }>;
      reset_list?: Record<string, number> | null;
      ts_crea?: string | null;
    };

    // refresh reset_list + ts_crea if the server had fresher values
    // (cheap, avoids a second round-trip to /api/terminal just for
    // calendar tints). reset_list is null if the column is empty.
    // NOTE: as of Phase 12.8b, reset_list is sourced from
    // /api/terminal and /api/data (computed by fetchResetList() in
    // the PHP backend), not from terminalacc. The override here is
    // a defensive belt-and-suspenders in case the values somehow
    // diverged (e.g. a manual SQL ALTER added a mach.reset_list
    // column and the row was re-fetched).
    if (payload.reset_list) t.reset_list = payload.reset_list;
    if (payload.ts_crea)    t.ts_crea    = payload.ts_crea;

    // Index the per-day diffs. acc_diff is what we sum across the
    // range (it's the day's delta, not the running bucket).
    for (const rec of payload.records || []) {
      if (!rec || !rec.dated) continue;
      // Prefer acc_diff for the "what happened" semantics. Fall back
      // to acc_long only if the spot terminal hasn't written a diff
      // for this row (older records before the diff column existed).
      const src = rec.acc_diff ?? rec.acc_long;
      if (src == null) continue;
      const norm = normaliseAccounting(src);
      if (norm) {
        // Override `st` with the record's `dated` (YYYY-MM-DD).
        // The acc_diff blob's `st`/`start_date` is sometimes a full
        // timestamp (e.g. "2026-06-03 09:57:01") which then leaks
        // into the chart X-axis labels via shortDate()'s
        // `.slice(5)`. Pinning `st` to the date makes the X axis
        // render as "MM-DD" cleanly, matching the group page.
        norm.st = rec.dated;
        cache.set(rec.dated, norm);
      }
    }
    getState(t).lastFetchedAt = new Date().toISOString();
  } finally {
    hideSpinner();
  }
}

// Sum a list of NormalisedAcc into a single NormalisedAcc. Money in is
// merged per ISO; the three game counters are summed by slot.
//
// Exported because the group-level Accounting page (admin/src/accounting-
// group.ts) needs the same summation over per-day aggregates from
// /api/groupacc. Pure helper, no Terminal state involved, so importing
// is cleaner than duplicating.
export function sumAccs(accs: NormalisedAcc[]): NormalisedAcc {
  const total: NormalisedAcc = {
    st: accs.length ? (accs[0].st || '') : '',
    in: { coin: {}, note: {} },
    token: { out: 0, hand: 0 },
    games: { count: [0, 0, 0], wins: [0, 0, 0] },
  };
  for (const a of accs) {
    for (const k of ['coin', 'note'] as const) {
      for (const [iso, v] of Object.entries(a.in?.[k] || {})) {
        total.in[k][iso] = (total.in[k][iso] || 0) + Number(v || 0);
      }
    }
    total.token.out += Number(a.token?.out || 0);
    total.token.hand += Number(a.token?.hand || 0);
    for (let i = 0; i < 3; i++) {
      total.games.count[i] += Number(a.games?.count?.[i] || 0);
      total.games.wins[i] += Number(a.games?.wins?.[i] || 0);
    }
  }
  return total;
}

// ---- Calendar render ----
function monthName(s: string): string {
  const d = parseYmd(s + 'T00:00:00');
  return d.toLocaleString('en', { month: 'long', year: 'numeric' });
}
const DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function renderMonth(
  parent: HTMLElement,
  monthYmd: string,
  t: Terminal,
  rs: RangedState,
  minYmdAllowed: string,
  maxYmdAllowed: string,
  onClickDate: (d: string) => void,
): void {
  const m = parseYmd(monthYmd + 'T00:00:00');
  const year = m.getFullYear();
  const mon  = m.getMonth();
  // First day-of-week in this month (0 = Sunday, 1 = Monday, ...).
  const firstDow = (new Date(year, mon, 1).getDay() + 6) % 7; // shift to Mon-first
  const daysInMonth = new Date(year, mon + 1, 0).getDate();

  const monthDiv = document.createElement('div');
  monthDiv.className = 'flex-1 min-w-0';

  const title = document.createElement('div');
  title.className = 'text-center font-medium text-gray-700 mb-2';
  title.textContent = monthName(monthYmd);
  monthDiv.appendChild(title);

  // DOW header
  const dowRow = document.createElement('div');
  dowRow.className = 'grid grid-cols-7 gap-1 text-[10px] text-gray-300 bg-gray-500 text-center mb-1 rounded-md p-1';
  for (const d of DOW) {
    const c = document.createElement('div');
    c.textContent = d;
    dowRow.appendChild(c);
  }
  monthDiv.appendChild(dowRow);

  // Day grid: pad with blank cells before the 1st, then real cells.
  const grid = document.createElement('div');
  grid.className = 'grid grid-cols-7 gap-0 bg-gray-100 rounded-md p-1';
  for (let i = 0; i < firstDow; i++) {
    const blank = document.createElement('div');
    grid.appendChild(blank);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const cell = document.createElement('button');
    cell.type = 'button';
    const ds = year + '-' + pad2(mon + 1) + '-' + pad2(day);
    const isMin = cmpYmd(ds, minYmdAllowed) < 0;
    const isMax = cmpYmd(ds, maxYmdAllowed) > 0;
    const disabled = isMin || isMax;
    // Range position. Five states:
    //   --picked : 'from' is set but 'to' is not (user has clicked
    //               once; the system is waiting for the second click).
    //               Visually identical to --start.
    //   --single : from == to == this day (one-day range, completed).
    //   --start  : the 'from' endpoint of a multi-day range.
    //   --mid    : strictly between from and to.
    //   --end    : the 'to' endpoint of a multi-day range.
    // We copy from/to into non-null locals so TS can see the
    // narrowing (the source fields are `string | null`).
    const from = rs.from, to = rs.to;
    const isPicked = !!from && !to && from === ds;     // only start chosen
    const hasRange = !!(from && to);
    const isSingle = hasRange && from === to && from === ds;
    const isStart  = hasRange && !isSingle && from === ds;
    const isEnd    = hasRange && !isSingle && to   === ds;
    const isMid    = hasRange && !isSingle && !isStart && !isEnd
                     && cmpYmd(ds, from) > 0 && cmpYmd(ds, to) < 0;

    cell.textContent = String(day);
    cell.dataset.date = ds;

    // One base class + one state modifier. All visual styling lives
    // in the .cal-cell / .cal-cell--* rules below; the only inline
    // style is the per-day reset-list gradient (alpha is computed).
    // --picked reuses --start's CSS rule (both selectors are listed
    // in the same block in admin/index.php), so the two states
    // look identical to the user.
    cell.className = 'cal-cell'
      + (disabled            ? ' cal-cell--disabled' : ' cal-cell--enabled')
      + (isPicked            ? ' cal-cell--picked cal-cell--start' : '')
      + (isSingle            ? ' cal-cell--single'   : '')
      + (isStart && !isPicked ? ' cal-cell--start'   : '')
      + (isMid               ? ' cal-cell--mid'      : '')
      + (isEnd               ? ' cal-cell--end'      : '');

    if (disabled) cell.disabled = true;

    // Reset-list gradient: violet background, intensity ∝ log(count+1)/log(max+1).
    // Inline style because the alpha is a per-cell computed value,
    // not a fixed class.
    const resetN = t.reset_list && t.reset_list[ds];
    if (resetN) {
      const maxR = maxResetCount(t);
      const intensity = maxR > 0 ? Math.log(resetN + 1) / Math.log(maxR + 1) : 0;
      // 0.18 minimum so even a count of 1 is visibly tinted.
      const alpha = Math.max(0.18, 0.18 + intensity * 0.55);
      cell.style.backgroundColor = 'rgba(139, 92, 246, ' + alpha.toFixed(2) + ')';
      // Count badge top-right (the gradient is the cell background;
      // the badge gives an explicit number on hover).
      const badge = document.createElement('span');
      badge.className = 'cal-cell__badge';
      badge.textContent = String(resetN);
      cell.appendChild(badge);
    }

    if (!disabled) {
      cell.addEventListener('click', () => onClickDate(ds));
    }
    grid.appendChild(cell);
  }
  monthDiv.appendChild(grid);
  parent.appendChild(monthDiv);
}

// ---- Public entry: render the Ranged tab ----
export async function renderAccountingRanged(
  state: TimestopState,
  container: HTMLElement,
  config: ApiFetchConfig,
): Promise<void> {
  container.textContent = '';
  const t = state.currentTerminal;
  if (!t) return;

  const rs = getState(t);

  // Range bounds:
  //   - lower: t.ts_crea (terminal creation date) OR 90 days back
  //     if we have no ts_crea (defensive default for older rows).
  //   - upper: today (no future dates).
  const today = ymd(new Date());
  const tsCrea = tsCreaToYmd(t.ts_crea);
  const minYmdAllowed = tsCrea
    ? tsCrea
    : (() => { const d = new Date(); d.setDate(d.getDate() - 90); return ymd(d); })();

  const wrapper = document.createElement('div');
  wrapper.className = 'space-y-4';

  // ---- Top bar: prev/next month + selected range label ----
  const top = document.createElement('div');
  top.className = 'flex items-center justify-between mx-2';
  const prev = document.createElement('button');
  prev.type = 'button';
  prev.className = 'px-3 py-1.5 rounded-xl text-sm bg-gray-100 hover:bg-gray-200';
  prev.textContent = i18n('tab_accounting.prev_month');
  prev.addEventListener('click', () => {
    rs.leftMonth = addMonths(rs.leftMonth, -1);
    void renderAccountingRanged(state, container, config);
  });
  const next = document.createElement('button');
  next.type = 'button';
  next.className = 'px-3 py-1.5 rounded-xl text-sm bg-gray-100 hover:bg-gray-200';
  next.textContent = i18n('tab_accounting.next_month');
  next.addEventListener('click', () => {
    rs.leftMonth = addMonths(rs.leftMonth, 1);
    void renderAccountingRanged(state, container, config);
  });
  const rangeLbl = document.createElement('div');
  rangeLbl.className = 'text-sm text-gray-600 font-mono';
  // MM-DD labels (no year) — same shape as the group Accounting
  // page's range label so the two pages read the same way.
  const fmtMmDd = (s: string): string => s.slice(5);
  if (rs.from && rs.to) rangeLbl.textContent = fmtMmDd(rs.from) + '  →  ' + fmtMmDd(rs.to);
  else if (rs.from)     rangeLbl.textContent = i18n('tab_accounting.from_n', { date: fmtMmDd(rs.from) });
  else                  rangeLbl.textContent = i18n('tab_accounting.click_start');
  top.appendChild(prev);
  top.appendChild(rangeLbl);
  top.appendChild(next);
  wrapper.appendChild(top);

  // ---- Calendar (2 months side-by-side) ----
  const cal = document.createElement('div');
  cal.className = 'flex gap-4 bg-gray-300 rounded-2xl p-3';
  const leftMonth  = rs.leftMonth;
  const rightMonth = addMonths(leftMonth, 1);

  // onClickDate — apply the click-to-set-range rules. Re-render
  // after each click so the highlights + range label update.
  const onClickDate = (ds: string): void => {
    if (cmpYmd(ds, minYmdAllowed) < 0 || cmpYmd(ds, today) > 0) return;
    if (!rs.from || (rs.from && rs.to)) {
      // No range, or both endpoints already set — start a new range.
      rs.from = ds;
      rs.to   = null;
    } else if (rs.from && !rs.to) {
      // We have a start, no end yet.
      if (ds === rs.from) {
        // Same-cell click as the start = single-day range
        // (from == to). The user can later click another cell to
        // widen it, or click any cell to start a fresh range.
        rs.to = rs.from;
      } else if (cmpYmd(ds, rs.from) < 0) {
        // Click is before the start — make it the new start.
        rs.from = ds;
      } else {
        rs.to = ds;
      }
    }
    void renderAccountingRanged(state, container, config);
  };

  renderMonth(cal, leftMonth,  t, rs, minYmdAllowed, today, onClickDate);
  renderMonth(cal, rightMonth, t, rs, minYmdAllowed, today, onClickDate);
  wrapper.appendChild(cal);

  // ---- Status row: legend + clear button + cache badge ----
  const status = document.createElement('div');
  status.className = 'flex items-center justify-between text-xs text-gray-500';
  const legend = document.createElement('div');
  legend.className = 'flex items-center gap-x-3';
  legend.innerHTML =
    '<span class="inline-flex items-center gap-x-1">' +
      '<span class="inline-block w-3 h-3 rounded bg-emerald-200"></span>' + i18n('tab_accounting.legend_in_range') +
    '</span>' +
    '<span class="inline-flex items-center gap-x-1">' +
      '<span class="inline-block w-3 h-3 rounded" style="background:rgba(139,92,246,0.55)"></span>' + i18n('tab_accounting.legend_resets') +
    '</span>';
  status.appendChild(legend);

  const right = document.createElement('div');
  right.className = 'flex items-center gap-x-3';
  if (rs.lastFetchedAt) {
    const fb = document.createElement('span');
    fb.textContent = i18n('tab_accounting.fetched_at', { time: new Date(rs.lastFetchedAt).toLocaleTimeString() });
    right.appendChild(fb);
  }
  if (rs.from || rs.to) {
    const clr = document.createElement('button');
    clr.type = 'button';
    clr.className = 'px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200';
    clr.textContent = i18n('tab_accounting.clear_range');
    clr.addEventListener('click', () => {
      rs.from = null;
      rs.to = null;
      void renderAccountingRanged(state, container, config);
    });
    right.appendChild(clr);
  }
  status.appendChild(right);
  wrapper.appendChild(status);

  // ---- Table: sum of cached acc_diffs in the range ----
  const tableHost = document.createElement('div');
  wrapper.appendChild(tableHost);
  container.appendChild(wrapper);

  if (rs.from && rs.to) {
    // Make sure the cache covers [from..to], fetching the gaps.
    const from = minYmd(rs.from, rs.to);
    const to   = maxYmd(rs.from, rs.to);
    try {
      await ensureRangeCached(t, from, to, config, state);
    } catch (e) {
      const errBox = document.createElement('div');
      errBox.className = 'bg-red-50 text-red-700 text-sm rounded-2xl p-4';
      errBox.textContent = i18n('tab_accounting.failed_to_load', { message: (e as Error).message });
      tableHost.appendChild(errBox);
      return;
    }

    // Walk the range, collect cached entries, sum.
    const accs: NormalisedAcc[] = [];
    let cur = from;
    while (cmpYmd(cur, to) <= 0) {
      const got = rs.cache.get(cur);
      if (got) accs.push(got);
      cur = addDays(cur, 1);
    }
    if (accs.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'bg-gray-100 rounded-2xl p-4 text-gray-400 text-sm text-center';
      empty.textContent = i18n('tab_accounting.no_data_in_range');
      tableHost.appendChild(empty);
      return;
    }
    const summed = sumAccs(accs);
    // Override `st` to show the actual range so makeAccHeader()
    // surfaces "Ranged    since 07-01 → 07-08 (8 days)". Use MM-DD
    // (no year) to match the calendar / range label above and the
    // group Accounting page's header.
    summed.st = from.slice(5) + ' → ' + to.slice(5) + ' (' + accs.length + ' ' + (accs.length === 1 ? i18n('common.day') : i18n('common.days')) + ')';
    const inner = document.createElement('div');
    inner.className = 'space-y-4';
    tableHost.appendChild(inner);
    inner.appendChild(makeAccHeader(i18n('tab_accounting.ranged_label'), summed.st));
    renderAccTable(inner, t, summed, state);
    // Phase 12.14: per-terminal charts below the table. Destroy
    // any prior chart instances first (the ranged tab rebuilds
    // on every range / prev / next click, so we MUST release the
    // previous ones or Chart.js will leak canvas + listeners).
    destroyRangedCharts();
    renderRangedCharts(inner, t, accs);
  } else {
    // No range yet — show a placeholder so the layout doesn't jump
    // when the user makes a selection.
    const ph = document.createElement('div');
    ph.className = 'bg-gray-100 rounded-2xl p-6 text-gray-400 text-sm text-center';
    ph.textContent = i18n('tab_accounting.pick_range');
    tableHost.appendChild(ph);
  }
}

// =====================================================================
// Phase 12.14: per-terminal Ranged charts.
// =====================================================================
// Three Chart.js charts are painted below the Ranged table:
//   1. Multi-line: total Money In per currency, one line per ISO,
//      one X point per day in the picked range.
//   2. Stacked bar: games played per game slot (3 series), same X axis.
//   3. Pie: share of games played per game slot over the range.
// All three are recreated on every range / prev / next click, so
// the prior instances are released via destroyRangedCharts() to
// avoid Chart.js leaks.

// Chart instance list — we keep the references so we can call
// .destroy() on the next re-render. Kept module-scope (same
// pattern as the per-tab Chart instance on state.modalChartInstance).
const _rangedCharts: Array<{ destroy: () => void }> = [];

function destroyRangedCharts(): void {
  while (_rangedCharts.length) {
    const c = _rangedCharts.pop();
    try { c && c.destroy(); } catch { /* already gone */ }
  }
}

// Chart.js is loaded as a global from the CDN <script> in
// admin/index.php. Access via window.Chart with a defensive guard
// so a missing CDN script doesn't blow up the whole page.
type ChartCtor = new (ctx: HTMLCanvasElement, config: Record<string, unknown>) => { destroy: () => void };
function getChartCtor(): ChartCtor | null {
  const w = window as unknown as { Chart?: ChartCtor };
  return typeof w.Chart === 'function' ? w.Chart : null;
}

// Light palette for the multi-line / pie chart series. Picked to
// be distinguishable in the gray-300 card background.
const SERIES_COLORS = [
  '#10b981', // emerald-500
  '#3b82f6', // blue-500
  '#f59e0b', // amber-500
  '#8b5cf6', // violet-500
  '#ef4444', // red-500
  '#06b6d4', // cyan-500
];

// Short-date label for the X axis: 'MM-DD'. Full 'YYYY-MM-DD' is
// too long when the range has 60+ days.
function shortDate(ymd: string): string { return ymd.slice(5); }

// Build the X-axis labels + ISO -> per-day series for the money-in
// multi-line chart. `accs` is in date order (we walked from..to).
function buildMoneyInSeries(
  accs: NormalisedAcc[],
  dates: string[],
  isos: string[],
): { labels: string[]; datasets: Array<{ label: string; data: number[]; borderColor: string; backgroundColor: string; tension: number; pointRadius: number; pointHoverRadius: number; borderWidth: number }> } {
  const labels = dates.map(shortDate);
  const datasets = isos.map((iso, i) => {
    const data: number[] = [];
    for (let d = 0; d < accs.length; d++) {
      const a = accs[d];
      // Money in = coins + notes. The cabinet takes no cards.
      const c = Number(a?.in?.coin?.[iso] || 0);
      const n = Number(a?.in?.note?.[iso] || 0);
      data.push(c + n);
    }
    const color = SERIES_COLORS[i % SERIES_COLORS.length];
    return {
      label: iso,
      data,
      borderColor: color,
      backgroundColor: color,
      tension: 0.25,
      // A single-day range is one point: with radius 0 the line
      // renders as an empty plot, so show the dot when short.
      pointRadius: data.length <= 1 ? 4 : 0,
      pointHoverRadius: 5,
      borderWidth: 2,
    };
  });
  return { labels, datasets };
}

// Build the per-game line series: 3 series, one per game slot, value =
// games played that day. Days not in cache contribute 0.
function buildGamesCountSeries(
  accs: NormalisedAcc[],
  dates: string[],
): { labels: string[]; datasets: Array<{ label: string; data: number[]; borderColor: string; backgroundColor: string; tension: number; pointRadius: number; pointHoverRadius: number; borderWidth: number }> } {
  const labels = dates.map(shortDate);
  const datasets = [0, 1, 2].map((idx) => {
    const data: number[] = [];
    for (let d = 0; d < accs.length; d++) {
      data.push(Number(accs[d]?.games?.count?.[idx] || 0));
    }
    const color = SERIES_COLORS[idx];
    return {
      label: i18n('tab_accounting.game_n', { n: (idx + 1) }),
      data,
      borderColor: color,
      backgroundColor: color,
      tension: 0.25,
      // A single-day range is one point: with radius 0 the line
      // renders as an empty plot, so show the dot when short.
      pointRadius: data.length <= 1 ? 4 : 0,
      pointHoverRadius: 5,
      borderWidth: 2,
    };
  });
  return { labels, datasets };
}

// Build the pie chart data: one slice per game slot, value = games
// played over the range. Legend labels include the count.
function buildGamesPieData(
  accs: NormalisedAcc[],
): { labels: string[]; datasets: Array<{ data: number[]; backgroundColor: string[]; borderColor: string; borderWidth: number }> } {
  const totals = [0, 0, 0];
  for (const a of accs) {
    for (let i = 0; i < 3; i++) {
      totals[i] += Number(a?.games?.count?.[i] || 0);
    }
  }
  return {
    labels: [
      i18n('tab_accounting.game_with_count', { n: 1, count: totals[0] }),
      i18n('tab_accounting.game_with_count', { n: 2, count: totals[1] }),
      i18n('tab_accounting.game_with_count', { n: 3, count: totals[2] }),
    ],
    datasets: [{
      data: totals,
      backgroundColor: SERIES_COLORS.slice(0, 3),
      borderColor: '#ffffff',
      borderWidth: 2,
    }],
  };
}

// Render a single chart onto a fresh <canvas> appended to `parent`,
// registering the instance for later destruction. Returns the
// canvas (or null if Chart isn't available).
function paintChart(
  parent: HTMLElement,
  ctor: ChartCtor,
  type: 'line' | 'bar' | 'pie',
  data: unknown,
  options: Record<string, unknown>,
  height: number,
): HTMLCanvasElement | null {
  const c = document.createElement('canvas');
  c.style.width = '100%';
  c.style.height = height + 'px';
  c.style.maxHeight = height + 'px';
  parent.appendChild(c);
  const inst = new ctor(c, { type, data, options });
  _rangedCharts.push(inst);
  return c;
}

// Public entry: paint the 3 charts inside `parent` (the Ranged
// tab's `inner` wrapper, which already has space-y-4).
function renderRangedCharts(
  parent: HTMLElement,
  t: Terminal,
  accs: NormalisedAcc[],
): void {
  if (accs.length === 0) return;
  const ctor = getChartCtor();
  if (!ctor) {
    // Defensive: Chart.js didn't load (e.g. CDN blocked). Surface
    // a one-line notice so the user knows the charts are missing
    // rather than seeing a silent empty space.
    const note = document.createElement('div');
    note.className = 'bg-gray-100 rounded-2xl p-3 text-xs text-gray-400 text-center';
    note.textContent = i18n('tab_accounting.charts_unavailable');
    parent.appendChild(note);
    return;
  }

  // X axis: same date list we walked to build `accs`. accs[i]
  // corresponds to dates[i] because we built accs in date order
  // (see the while loop above that walks from..to).
  const dates: string[] = [];
  // Reconstruct dates from accs[].st (each is a YYYY-MM-DD set
  // by /api/terminalacc as the `dated` column). Fall back to
  // empty if the server omitted st.
  for (const a of accs) dates.push(a.st || '');

  // ISOs in use across the range. Build the union from accs
  // (not from ct.currencyLookup) so currencies that exist on the
  // terminal but had no activity in the range don't get an empty
  // line. We DO include all configured ISOs in the rendered set
  // if any have activity, for a consistent legend.
  const isos = new Set<string>();
  for (const a of accs) {
    for (const ch of ['coin', 'note'] as const) {
      const m = a?.in?.[ch] || {};
      for (const iso of Object.keys(m)) isos.add(iso);
    }
  }
  const isoList = Array.from(isos);

  // Card 1: Money in (multi-line)
  if (isoList.length > 0) {
    const card1 = document.createElement('div');
    card1.className = 'bg-white border rounded-2xl p-4';
    const h1 = document.createElement('div');
    h1.className = 'text-sm font-semibold text-gray-700 mb-2';
    h1.textContent = i18n('tab_accounting.chart_money_in_title');
    card1.appendChild(h1);
    paintChart(
      card1,
      ctor,
      'line',
      buildMoneyInSeries(accs, dates, isoList),
      {
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
        scales: {
          y: { beginAtZero: true, grid: { color: '#f1f5f9' } },
          x: { grid: { color: '#f1f5f9' } },
        },
        responsive: true,
        maintainAspectRatio: false,
      },
      220,
    );
    parent.appendChild(card1);
  }

// Card 2: Games per slot (3-series STACKED BAR) — one bar per day,
// segments = Game 1 / 2 / 3 stacked. The total bar height is the day's
// games played; segments show how much each game type contributed.
const card2 = document.createElement('div');
card2.className = 'bg-white border rounded-2xl p-4';
const h2 = document.createElement('div');
h2.className = 'text-sm font-semibold text-gray-700 mb-2';
h2.textContent = i18n('tab_accounting.chart_games_title');
card2.appendChild(h2);
paintChart(
  card2,
  ctor,
  'bar',
  buildGamesCountSeries(accs, dates),
  {
    plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
    scales: {
      x: { stacked: true, grid: { color: '#f1f5f9' } },
      y: { stacked: true, beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { precision: 0 } },
    },
    responsive: true,
    maintainAspectRatio: false,
  },
  220,
);
parent.appendChild(card2);

  // Card 3: Pie of game slots (sum of games played)
  const card3 = document.createElement('div');
  card3.className = 'bg-white border rounded-2xl p-4';
  const h3 = document.createElement('div');
  h3.className = 'text-sm font-semibold text-gray-700 mb-2';
  h3.textContent = i18n('tab_accounting.chart_games_share_title');
  card3.appendChild(h3);
  paintChart(
    card3,
    ctor,
    'pie',
    buildGamesPieData(accs),
    {
      plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
      responsive: true,
      maintainAspectRatio: false,
    },
    220,
  );
  parent.appendChild(card3);
}

// Also export destroyRangedCharts so clearRangedState() can release
// chart instances when the modal closes (defensive — the modal is
// re-opened with a fresh terminal, but the instances would otherwise
// stay referenced by Chart.js's internal registry).
export { destroyRangedCharts };

